import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { handleAuthCallback } from '../lib/platformAuth';

const IntegrationsCallback = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState('Processing your request...');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const processCallback = async () => {
      try {
        // Get the full URL including the hash
        const fullUrl = window.location.href;
        console.log('Processing callback URL:', fullUrl);

        // Parse parameters from the hash portion if it exists
        const hashParams = new URLSearchParams(
          window.location.hash.substring(window.location.hash.indexOf('?'))
        );
        
        // Parse parameters from the search portion
        const searchParams = new URLSearchParams(window.location.search);

        // Combine parameters, prioritizing hash parameters
        const params = new URLSearchParams();
        for (const [key, value] of searchParams.entries()) {
          params.append(key, value);
        }
        for (const [key, value] of hashParams.entries()) {
          params.append(key, value);
        }

        // Check for error parameters first
        const error = params.get('error');
        const errorDescription = params.get('error_description');
        
        if (error || errorDescription) {
          throw new Error(errorDescription || error || 'Connection failed');
        }

        // Check required parameters
        const code = params.get('code');
        const state = params.get('state');

        if (!code || !state) {
          throw new Error('Required parameters are missing from the callback URL');
        }

        setStatus('Completing connection...');
        const result = await handleAuthCallback(params);

        if (!result.success) {
          throw new Error(result.error || 'Connection failed');
        }

        setStatus('Connection successful!');
        toast.success('Platform connected successfully');

        // Immediate redirect to dashboard
        navigate('/dashboard');
      } catch (error) {
        console.error('Callback processing error:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to complete platform connection';
        setError(errorMessage);
        setStatus('Connection failed');
        toast.error(errorMessage);

        // Short delay before redirecting on error
        setTimeout(() => {
          navigate('/dashboard/integrations');
        }, 2000);
      }
    };

    processCallback();
  }, [location, navigate]);

  if (error) {
    return (
      <div className="min-h-[400px] flex flex-col items-center justify-center p-8">
        <div className="max-w-md w-full bg-[#151632]/50 backdrop-blur-sm p-8 rounded-xl border border-[#1E2048]">
          <div className="text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">{status}</h3>
            <p className="text-red-400 text-sm mb-4">{error}</p>
            <p className="text-gray-400 text-sm">
              Redirecting you back to integrations...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[400px] flex flex-col items-center justify-center p-8">
      <div className="max-w-md w-full bg-[#151632]/50 backdrop-blur-sm p-8 rounded-xl border border-[#1E2048]">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-blue-500 mx-auto mb-4 animate-spin" />
          <h3 className="text-xl font-semibold text-white mb-2">{status}</h3>
          <p className="text-gray-400 text-sm">
            Please wait while we complete your connection...
          </p>
        </div>
      </div>
    </div>
  );
};

export default IntegrationsCallback;